import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/api";

export const QuoteLines = ({ lines, setLines }) => {
  const upd = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const del = (i) => setLines(lines.filter((_, j) => j !== i));
  const mobile = useIsMobile();

  if (mobile) return <MobileLines lines={lines} setLines={setLines} upd={upd} del={del} />;
  return (
    <table className="w-full text-sm" data-testid="quote-lines-table">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr>
          <th className="px-3 py-2.5">Item</th><th className="w-24 px-3 py-2.5">Qty</th><th className="w-32 px-3 py-2.5">Unit (ex GST)</th>
          <th className="w-40 px-3 py-2.5">Discount</th><th className="w-20 px-3 py-2.5">GST</th><th className="w-32 px-3 py-2.5 text-right">Amount</th><th className="w-10" />
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => {
          const amt = lineAmount(l);
          const low = l.stock != null && Number(l.qty) > l.stock;
          return (
            <tr key={i} className="border-t border-slate-100 fade-up" data-testid={`quote-line-${i}`}>
              <td className="px-3 py-2.5">
                <div className="font-medium text-[#0B1523]">{l.name}</div>
                <div className="text-xs text-slate-500">
                  <span className="num">{l.sku}</span>
                  {l.stock != null && <span className={low ? "ml-2 font-semibold text-red-600" : "ml-2"}>Stock: {l.stock}</span>}
                </div>
              </td>
              <td className="px-3 py-2.5">
                <Input type="number" min="0" step="any" value={l.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="num h-9" data-testid={`quote-line-qty-${i}`} />
              </td>
              <td className="px-3 py-2.5">
                <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className="num h-9" data-testid={`quote-line-price-${i}`} />
              </td>
              <td className="px-3 py-2.5">
                <div className="flex">
                  <Input type="number" min="0" step="0.01" value={l.discount ?? ""} placeholder="0" onChange={(e) => upd(i, "discount", e.target.value)} className="num h-9 rounded-r-none" data-testid={`quote-line-discount-${i}`} />
                  <Button type="button" variant="outline" className="h-9 w-11 rounded-l-none border-l-0 px-0 font-semibold text-[#01195D]" onClick={() => upd(i, "discount_type", l.discount_type === "amt" ? "pct" : "amt")} data-testid={`quote-line-discount-type-${i}`}>
                    {l.discount_type === "amt" ? "$" : "%"}
                  </Button>
                </div>
              </td>
              <td className="px-3 py-2.5">
                <Switch checked={l.has_gst} onCheckedChange={(v) => upd(i, "has_gst", v)} data-testid={`quote-line-gst-${i}`} />
              </td>
              <td className="num px-3 py-2.5 text-right font-medium" data-testid={`quote-line-amount-${i}`}>{money(amt)}</td>
              <td className="px-1">
                <Button size="icon" variant="ghost" onClick={() => del(i)} data-testid={`quote-line-remove-${i}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
              </td>
            </tr>
          );
        })}
        {!lines.length && (
          <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">Add items to this quote.</td></tr>
        )}
      </tbody>
      {lines.length > 0 && <DiscountAll lines={lines} setLines={setLines} />}
    </table>
  );
};

const useIsMobile = () => {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
};

const DiscountAllControl = ({ lines, setLines }) => {
  const [pct, setPct] = useState("");
  const apply = () => setLines(lines.map((l) => ({ ...l, discount: Number(pct) || 0, discount_type: "pct" })));
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 text-sm text-slate-600">
      <span>Discount all lines</span>
      <Input type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="%" className="num h-8 w-20" data-testid="quote-discount-all-input" />
      <span>%</span>
      <Button type="button" size="sm" variant="outline" onClick={apply} data-testid="quote-discount-all-btn">Apply</Button>
    </div>
  );
};

const DiscountAll = (props) => (
  <tfoot>
    <tr className="border-t border-slate-200 bg-slate-50">
      <td colSpan={7} className="px-3 py-2.5"><DiscountAllControl {...props} /></td>
    </tr>
  </tfoot>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);

const MobileLines = ({ lines, setLines, upd, del }) => (
  <div data-testid="quote-lines-table">
    {lines.map((l, i) => {
      const low = l.stock != null && Number(l.qty) > l.stock;
      return (
        <div key={i} className="border-t border-slate-100 p-4 first:border-t-0" data-testid={`quote-line-${i}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-[#0B1523]">{l.name}</div>
              <div className="text-xs text-slate-500">
                <span className="num">{l.sku}</span>
                {l.stock != null && <span className={low ? "ml-2 font-semibold text-red-600" : "ml-2"}>Stock: {l.stock}</span>}
              </div>
            </div>
            <Button size="icon" variant="ghost" className="-mr-2 shrink-0" onClick={() => del(i)} data-testid={`quote-line-remove-${i}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="Qty"><Input type="number" inputMode="decimal" min="0" step="any" value={l.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="num h-10" data-testid={`quote-line-qty-${i}`} /></Field>
            <Field label="Unit (ex GST)"><Input type="number" inputMode="decimal" min="0" step="0.01" value={l.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className="num h-10" data-testid={`quote-line-price-${i}`} /></Field>
            <Field label="Discount">
              <div className="flex">
                <Input type="number" inputMode="decimal" min="0" step="0.01" value={l.discount ?? ""} placeholder="0" onChange={(e) => upd(i, "discount", e.target.value)} className="num h-10 rounded-r-none" data-testid={`quote-line-discount-${i}`} />
                <Button type="button" variant="outline" className="h-10 w-11 shrink-0 rounded-l-none border-l-0 px-0 font-semibold text-[#01195D]" onClick={() => upd(i, "discount_type", l.discount_type === "amt" ? "pct" : "amt")} data-testid={`quote-line-discount-type-${i}`}>
                  {l.discount_type === "amt" ? "$" : "%"}
                </Button>
              </div>
            </Field>
            <Field label="GST"><div className="flex h-10 items-center"><Switch checked={l.has_gst} onCheckedChange={(v) => upd(i, "has_gst", v)} data-testid={`quote-line-gst-${i}`} /></div></Field>
          </div>
          <div className="mt-3 flex justify-between text-sm">
            <span className="text-slate-500">Amount</span>
            <span className="num font-semibold" data-testid={`quote-line-amount-${i}`}>{money(lineAmount(l))}</span>
          </div>
        </div>
      );
    })}
    {!lines.length && <div className="px-3 py-10 text-center text-sm text-slate-500">Add items to this quote.</div>}
    {lines.length > 0 && <div className="border-t border-slate-200 bg-slate-50 p-3"><DiscountAllControl lines={lines} setLines={setLines} /></div>}
  </div>
);

export const lineAmount = (l) => {
  const gross = Math.round((Number(l.qty) || 0) * (Number(l.unit_price) || 0) * 100) / 100;
  const d = Number(l.discount) || 0;
  const disc = l.discount_type === "amt" ? Math.min(d, gross) : (gross * Math.min(d, 100)) / 100;
  return Math.round((gross - disc) * 100) / 100;
};

export const calcTotals = (lines) => {
  let sub = 0, gst = 0, cost = 0, disc = 0;
  lines.forEach((l) => {
    const a = lineAmount(l);
    disc += Math.round((Number(l.qty) || 0) * (Number(l.unit_price) || 0) * 100) / 100 - a;
    sub += a;
    if (l.has_gst) gst += Math.round(a * 10) / 100;
  });
  lines.forEach((l) => { cost += (Number(l.qty) || 0) * (Number(l.cost_price) || 0); });
  return { sub, gst, total: sub + gst, cost, gp: sub - cost, disc };
};
